import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  ClipboardList,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  LogOut,
  BookOpen
} from 'lucide-react';
import './AdminLayout.css';
import { AdminProvider, useAdminContext } from '../../context/AdminContext';
import adminApi from '../../utils/adminRequest';

// 导入功能组件
import AdminDashboard from '../../pages/AdminDashboard';
import AdminUsers from '../../pages/AdminUsers';
import AdminTicketList from '../../pages/AdminTicketList';
import AdminTicketDetail from '../../pages/AdminTicketDetail';
import FeedbackManagement from './FeedbackManagement';
import KnowledgeBase from './KnowledgeBase';
import SupportStatistics from './SupportStatistics';
import OperationDashboard from '../../pages/OperationDashboard';

const AdminLayoutContent = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const { activeView, switchView } = useAdminContext();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // 检查管理员登录状态
    const adminToken = localStorage.getItem('admin_token');
    const adminUserData = localStorage.getItem('admin_user');
    
    if (!adminToken || !adminUserData) {
      navigate('/sys-portal/login');
      return;
    }

    try {
      const user = JSON.parse(adminUserData);
      setAdminUser(user);
      
      // 只在第一次加载时设置默认视图
      if (!isInitialized) {
        if (user.role === 'support_admin') {
          switchView('tickets');
        } else if (user.role === 'operation_admin') {
          switchView('operation-dashboard');
        }
        setIsInitialized(true);
      }
    } catch (error) {
      console.error('解析管理员数据失败:', error);
      // 数据损坏，清理并跳转到登录页
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/sys-portal/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = async () => {
    try {
      // 调用后端登出API，将token加入黑名单
      await adminApi.post('/api/v1/admin/logout');
    } catch (error) {
      // 即使API调用失败，也继续清理本地存储
      console.warn('登出API调用失败，但仍会清理本地存储:', error);
    } finally {
      // 清理本地存储
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/sys-portal/login');
    }
  };

  // 根据管理员角色配置菜单项
  const getMenuItems = (role) => {
    // 运营管理员功能 - 独立功能菜单
    if (role === 'operation_admin') {
      return [
        {
          key: 'operation-dashboard',
          label: '运营看板',
          icon: LayoutDashboard
        },
        {
          key: 'operation-users',
          label: '用户管理',
          icon: Users
        },
        {
          key: 'operation-content',
          label: '内容发布',
          icon: FileText
        },
        {
          key: 'operation-classic-questions',
          label: '经典题库',
          icon: BookOpen
        }
      ];
    }

    // 客服管理员功能 - 精简后的菜单
    if (role === 'support_admin') {
      return [
        {
          key: 'tickets',
          label: '工单管理',
          icon: ClipboardList
        },
        {
          key: 'feedback',
          label: '反馈管理',
          icon: MessageSquare
        }
        // 移除了知识库和客服统计，只保留核心的工单和反馈功能
      ];
    }

    // 默认返回空数组（应该不会到达这里，因为所有角色都已处理）
    return [];
  };

  const menuItems = getMenuItems(adminUser?.role);

  // 检查是否是工单详情页
  const isTicketDetailPage = location.pathname.startsWith('/sys-portal/tickets/') && location.pathname !== '/sys-portal/tickets';

  // 切换视图
  const handleMenuClick = (key) => {
    // 如果当前在工单详情页面，需要先导航到对应的路由
    if (isTicketDetailPage) {
      if (key === 'tickets') {
        // 导航到工单列表页面
        navigate('/sys-portal/tickets');
      } else if (key === 'feedback') {
        // 导航到管理后台首页，然后切换视图
        navigate('/sys-portal');
      } else {
        // 其他视图也导航到管理后台首页
        navigate('/sys-portal');
      }
    }
    // 切换视图
    switchView(key);
  };
  
  // 渲染当前激活的视图
  const renderActiveView = () => {
    // 如果是工单详情页，直接显示详情页面
    if (isTicketDetailPage) {
      return <AdminTicketDetail />;
    }
    
    // 运营管理员的专属视图
    const operationViews = ['operation-dashboard', 'operation-users', 'operation-content', 'operation-classic-questions'];
    if (adminUser?.role === 'operation_admin' && operationViews.includes(activeView)) {
      return <OperationDashboard initialTab={activeView} />;
    }

    // 检查权限：只有客服管理员可以访问客服功能
    const supportOnlyViews = ['tickets', 'feedback'];
    if (adminUser?.role !== 'support_admin' && supportOnlyViews.includes(activeView)) {
      // 运营管理员默认显示运营看板
      if (adminUser?.role === 'operation_admin') {
        return <OperationDashboard initialTab="operation-dashboard" />;
      }
      return <AdminDashboard />;
    }

    // 检查权限：客服管理员不能访问用户管理
    if (adminUser?.role === 'support_admin' && activeView === 'users') {
      // 客服管理员默认显示工单管理
      return <AdminTicketList />;
    }

    switch (activeView) {
      case 'dashboard':
        return <AdminDashboard />;
      case 'users':
        return <AdminUsers />;
      case 'tickets':
        return <AdminTicketList />;
      case 'feedback':
        return <FeedbackManagement />;
      default:
        // 运营管理员默认显示运营看板
        if (adminUser?.role === 'operation_admin') {
          return <OperationDashboard initialTab="operation-dashboard" />;
        }
        // 客服管理员默认显示工单管理
        if (adminUser?.role === 'support_admin') {
          return <AdminTicketList />;
        }
        return <AdminDashboard />;
    }
  };

  const renderMenuItem = (item) => {
    const isActive = activeView === item.key;
    const Icon = item.icon;
    
    return (
      <div 
        key={item.key} 
        className={`menu-item ${isActive ? 'active' : ''}`}
        onClick={() => handleMenuClick(item.key)}
        title={sidebarCollapsed ? item.label : ''}
      >
        <div className="menu-item-header">
          {Icon && <Icon size={20} className="menu-icon" />}
          {!sidebarCollapsed && <span className="menu-label">{item.label}</span>}
        </div>
      </div>
    );
  };

  if (!adminUser) {
    return <div className="loading-container">加载中...</div>;
  }

  return (
    <div className={`admin-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* 侧边栏 */}
      <div className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="header-brand">
            <div className="brand-logo">
              <img src="/images/logos/logo.svg" alt="职唛AI" width="24" height="24" />
            </div>
            {!sidebarCollapsed && <span className="brand-text">职唛AI</span>}
          </div>
          <button 
            className="collapse-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>
        
        <div className="sidebar-menu">
          {menuItems.map(renderMenuItem)}
        </div>
        
        <div className="sidebar-footer">
          <div className="admin-info">
            <div className="admin-avatar">
              {adminUser.nickname.charAt(0)}
            </div>
            {!sidebarCollapsed && (
              <div className="admin-details">
                <div className="admin-name">{adminUser.nickname}</div>
                <div className="admin-role">
                  {adminUser.role === 'operation_admin' ? '运营管理员' : '客服管理员'}
                </div>
              </div>
            )}
          </div>
          <button className="logout-btn" onClick={handleLogout} title="退出登录">
            {sidebarCollapsed ? <LogOut size={18} /> : '退出登录'}
          </button>
        </div>
      </div>

      {/* 主内容区 */}
      <div className="admin-main">
        <div className="admin-header">
          <h1>管理后台</h1>
          <div className="header-actions">
            <span className="welcome-text">
              欢迎，{adminUser.nickname}
            </span>
          </div>
        </div>
        
        <div className="admin-content">
          {renderActiveView()}
        </div>
      </div>

      {/* 退出登录确认对话框 */}
      {showLogoutConfirm && (
        <div className="confirm-dialog-overlay">
          <div className="confirm-dialog">
            <button 
              className="confirm-dialog-close"
              onClick={() => setShowLogoutConfirm(false)}
            >
              ×
            </button>
            <h3 className="confirm-dialog-title">退出登录</h3>
            <p className="confirm-dialog-message">确定要退出吗？</p>
            <div className="confirm-dialog-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowLogoutConfirm(false)}
              >
                取消
              </button>
              <button 
                className="btn btn-danger"
                onClick={confirmLogout}
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminLayout = () => {
  return (
    <AdminProvider>
      <AdminLayoutContent />
    </AdminProvider>
  );
};

export default AdminLayout;
